import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, Alert, Divider, TextField } from '@mui/material';

const VehicleApiDebug = () => {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [apiBaseUrl, setApiBaseUrl] = useState(process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000');

  const testEndpoints = [
    { name: 'Get Vehicles', url: '/api/v1/admin/vehicles', method: 'GET' },
    { name: 'Get Maintenance Due', url: '/api/v1/admin/vehicles/maintenance-due', method: 'GET' },
    { name: 'Get Utilization', url: '/api/v1/admin/vehicles/utilization', method: 'GET' }
  ];

  const testEndpoint = async (endpoint) => {
    setLoading(prev => ({ ...prev, [endpoint.name]: true }));
    setErrors(prev => ({ ...prev, [endpoint.name]: null }));
    
    try {
      console.log(`Testing endpoint: ${apiBaseUrl}${endpoint.url}`);
      
      const response = await fetch(`${apiBaseUrl}${endpoint.url}`, {
        method: endpoint.method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      setResults(prev => ({ 
        ...prev, 
        [endpoint.name]: {
          status: response.status,
          data: data
        }
      }));
      
      console.log(`Success for ${endpoint.name}:`, data);
    } catch (error) {
      console.error(`Error for ${endpoint.name}:`, error);
      
      setErrors(prev => ({ 
        ...prev, 
        [endpoint.name]: {
          message: error.message,
          response: error.response ? {
            status: error.response.status,
            data: error.response.data
          } : null
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [endpoint.name]: false }));
    }
  };

  const testAllEndpoints = () => {
    testEndpoints.forEach(endpoint => testEndpoint(endpoint));
  };

  const handleTokenChange = (e) => {
    setToken(e.target.value);
  };

  const handleApiBaseUrlChange = (e) => {
    setApiBaseUrl(e.target.value);
  };
  
  const handleLogin = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: 'admin@example.com',
          password: 'password'
        })
      });
      
      const data = await response.json();
      console.log('Login response:', data);
      
      if (data.success && data.data && data.data.token) {
        setToken(data.data.token);
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data.user));
        alert('Login successful');
      } else if (data.token) {
        setToken(data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        alert('Login successful');
      } else {
        alert('Login failed: No token received');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert(`Login error: ${error.message}`);
    }
  };

  // Get token from localStorage on component mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Vehicle API Debug Tool</Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>API Configuration</Typography>
        <TextField
          label="API Base URL"
          variant="outlined"
          fullWidth
          value={apiBaseUrl}
          onChange={handleApiBaseUrlChange}
          sx={{ mb: 2 }}
        />
        <TextField
          label="JWT Token"
          variant="outlined"
          fullWidth
          value={token}
          onChange={handleTokenChange}
          sx={{ mb: 2 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          API Base URL: {apiBaseUrl || 'Not defined'}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={testAllEndpoints}
          sx={{ mr: 2 }}
        >
          Test All Endpoints
        </Button>
        <Button 
          variant="contained" 
          color="secondary" 
          onClick={handleLogin}
        >
          Login as Admin
        </Button>
      </Paper>

      {testEndpoints.map((endpoint) => (
        <Paper key={endpoint.name} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{endpoint.name}</Typography>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {endpoint.method} {endpoint.url}
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => testEndpoint(endpoint)}
                disabled={loading[endpoint.name]}
              >
                {loading[endpoint.name] ? <CircularProgress size={24} /> : 'Test'}
              </Button>
            </Box>
          </Box>
          
          {errors[endpoint.name] && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors[endpoint.name].message}
              {errors[endpoint.name].response && (
                <Typography variant="body2">
                  Status: {errors[endpoint.name].response.status}
                </Typography>
              )}
            </Alert>
          )}
          
          {results[endpoint.name] && (
            <Box>
              <Typography variant="subtitle2">Status: {results[endpoint.name].status}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Response:</Typography>
              <Box 
                component="pre" 
                sx={{ 
                  p: 1, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: '300px'
                }}
              >
                {JSON.stringify(results[endpoint.name].data, null, 2)}
              </Box>
            </Box>
          )}
        </Paper>
      ))}
    </Box>
  );
};

export default VehicleApiDebug;
